import WorkingHours from '@/components/availability/WorkingHours';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Send, RefreshCw, Unlink2, Copy, Check, Lock, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Input from '@/components/ui/Input';
import { useAuth } from '@/hooks/use-auth';
import MyChannelsCard from '@/components/connect/MyChannelsCard';
import OffDaysCard from '@/components/availability/OffDaysCard';
import {
  fetchTelegramStatus, fetchTelegramLinkCode, rotateTelegramLinkCode,
  unlinkTelegram, setPassword, updateMyProfile,
} from '@/lib/queries';

// Hard-coded for now - the bot username is fixed per deployment. If you ever
// rename the bot, update this constant and ship a new build.
const BOT_USERNAME = 'KAFProBot';

export default function ProfilePage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [pendingUnlink, setPendingUnlink] = useState(false);
  const [copied, setCopied] = useState(false);

  const status = useQuery({ queryKey: ['telegram-status'], queryFn: fetchTelegramStatus });
  const code = useQuery({
    queryKey: ['telegram-link-code'],
    queryFn: fetchTelegramLinkCode,
    enabled: status.data ? !status.data.linked : false,
  });

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const setPw = useMutation({
    mutationFn: () => setPassword({
      current_password: currentPw || undefined,
      new_password: newPw,
    }),
    onSuccess: () => {
      toast.success('Password updated. Sign in with your email + password next time.');
      setCurrentPw(''); setNewPw(''); setNewPw2('');
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Update failed';
      toast.error(msg);
    },
  });

  const rotate = useMutation({
    mutationFn: rotateTelegramLinkCode,
    onSuccess: () => { toast.success('New code'); qc.invalidateQueries({ queryKey: ['telegram-link-code'] }); },
  });
  const unlink = useMutation({
    mutationFn: unlinkTelegram,
    onSuccess: () => {
      toast.success('Telegram unlinked');
      setPendingUnlink(false);
      qc.invalidateQueries({ queryKey: ['telegram-status'] });
      qc.invalidateQueries({ queryKey: ['telegram-link-code'] });
    },
  });

  function copyCode() {
    if (!code.data?.code) return;
    // Copy the full command - the user pastes it straight into Telegram
    navigator.clipboard.writeText(`/link ${code.data.code}`).then(() => {
      setCopied(true);
      toast.success('Copied "/link ' + code.data!.code + '"');
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error('Copy failed'));
  }

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Profile"
        subtitle={user?.email}
      />

      <MyChannelsCard name={user?.display_name ?? ''} />
      <PhoneCard initial={user?.phone ?? ''} />
      {user?.role !== 'client' && user?.role !== 'freelancer' && <><HoursCard /><OffDaysCard /></>}

      <Card>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0">
            <Send size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-muted-strong">Telegram notifications</h3>
            <p className="text-xs text-muted mt-0.5">
              Get DMs when there's a review, mention, reply, or status change on your work.
            </p>
          </div>
        </div>

        {status.isLoading && <div className="text-sm text-muted py-4">Loading...</div>}

        {status.data?.linked && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="flex items-center gap-2 text-sm text-emerald-300 mb-1">
              <Check size={14} /> Linked to Telegram
              {status.data.linked_at && (
                <span className="text-emerald-400 text-[11px]">
                  - {new Date(status.data.linked_at).toLocaleString()}
                </span>
              )}
            </div>
            <p className="text-xs text-muted">
              You'll receive bot messages for reviews you're tagged on, replies in your threads, and status changes.
            </p>
            <div className="mt-3">
              <Button variant="ghost" size="sm" icon={<Unlink2 size={12} />} onClick={() => setPendingUnlink(true)}>
                Unlink
              </Button>
            </div>
          </div>
        )}

        {status.data && !status.data.linked && (
          <div className="space-y-3">
            <ol className="space-y-2 text-sm text-muted-strong">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-accent-600/30 text-accent-400 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                <span>
                  Open Telegram and search for <a
                    href={`https://t.me/${BOT_USERNAME}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent-400 hover:underline"
                  >@{BOT_USERNAME}</a>, or scan the QR you've been given. Press <span className="font-mono text-accent-400">Start</span>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-accent-600/30 text-accent-400 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                <span>Send the bot this message:</span>
              </li>
            </ol>

            {code.isLoading && <div className="text-sm text-muted py-4">Generating code...</div>}
            {code.data && (
              <div className="rounded-lg border border-slate-800 bg-slate-950 p-3 flex items-center gap-3">
                <code className="text-base font-mono text-accent-300 flex-1 select-all">/link {code.data.code}</code>
                <button
                  onClick={copyCode}
                  className="text-muted hover:text-muted-strong p-2 rounded transition"
                  title="Copy"
                >
                  {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
                </button>
                <button
                  onClick={() => rotate.mutate()}
                  className="text-muted hover:text-muted-strong p-2 rounded transition"
                  title="New code"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
            )}
            <p className="text-[11px] text-muted">
              The code expires when used or replaced. Once you've sent the message, refresh this page - the link should appear within a couple of seconds.
            </p>
          </div>
        )}
      </Card>

      <Card className="mt-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0">
            <Lock size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-muted-strong">Password</h3>
            <p className="text-xs text-muted mt-0.5">
              Sign in with email + password as an alternative to Google. Recommended when Google is unavailable or you want a permanent fallback.
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); if (newPw && newPw === newPw2 && newPw.length >= 8) setPw.mutate(); }}
          className="space-y-2.5 max-w-md"
        >
          <input
            type="password"
            placeholder="Current password (leave empty if setting for the first time)"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            autoComplete="current-password"
            className="w-full h-10 px-3 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
          />
          <input
            type="password"
            placeholder="New password (8+ characters)"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password"
            className="w-full h-10 px-3 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={newPw2}
            onChange={(e) => setNewPw2(e.target.value)}
            autoComplete="new-password"
            className="w-full h-10 px-3 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
          />
          {newPw && newPw !== newPw2 && (
            <div className="text-xs text-amber-400">Passwords don't match</div>
          )}
          {newPw && newPw.length < 8 && (
            <div className="text-xs text-amber-400">Use 8 or more characters</div>
          )}
          <Button
            type="submit"
            disabled={setPw.isPending || !newPw || newPw !== newPw2 || newPw.length < 8}
            loading={setPw.isPending}
          >
            Set password
          </Button>
        </form>
      </Card>

      <ConfirmDialog
        open={pendingUnlink}
        title="Unlink Telegram?"
        message="You'll stop receiving bot messages. You can re-link any time with a new code."
        confirmLabel="Unlink"
        cancelLabel="Keep linked"
        tone="warning"
        onCancel={() => setPendingUnlink(false)}
        onConfirm={() => unlink.mutate()}
      />
    </div>
  );
}

/** The phone the crew calls from the shoot page (WhatsApp or a plain call). */
function PhoneCard({ initial }: { initial: string }) {
  const [phone, setPhone] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const save = useMutation({
    mutationFn: () => updateMyProfile({ phone: phone.trim() || null }),
    onSuccess: (r) => { setSaved(r.phone ?? ''); setPhone(r.phone ?? ''); toast.success(r.phone ? 'Phone saved' : 'Phone removed'); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not save'),
  });
  return (
    <Card className="mb-4" data-testid="phone-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0"><Phone size={18} /></div>
        <div className="flex-1">
          <h3 className="font-medium text-muted-strong">Phone for the set</h3>
          <p className="text-xs text-muted mt-0.5">With the country code. The crew presses Call on the shoot page and reaches you on WhatsApp.</p>
        </div>
      </div>
      <div className="flex gap-2 items-end">
        <Input label="Phone" placeholder="+971 50 123 4567" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" />
        <Button size="sm" disabled={phone.trim() === saved.trim() || save.isPending} loading={save.isPending} onClick={() => save.mutate()} data-testid="profile-phone-save">Save</Button>
      </div>
    </Card>
  );
}


/** This person's working hours, per day and per date (components/availability/WorkingHours). */
function HoursCard() {
  return <WorkingHours />;
}
